import { useSelector } from 'react-redux';
import React, { useState, Fragment, useEffect } from 'react';

import AddIcon from '@mui/icons-material/Add';
import { Grid, Box, Divider } from '@mui/material';

import {
    Button,
    DataTable,
    Typography,
    CircleLoading,
    SnackBarAlert,
    TablePagination,
} from 'shared';
import UserForm from './UsersForm';
import { usersStyles } from './style/style';
import { handleErrorAlert } from 'utils/handleErrorAlert';
import { userTableHeaders } from './utils/userTableHeaders';
import { handleSuccessAlert } from 'utils/handleSuccessAlert';
import { useCompanyService } from 'store/services/company.service';

const Users = () => {
    const classes = usersStyles();
    const companyData = useSelector((state) => state.company.data);
    const { getCompanyUsers, updateCompanyUserStatus } = useCompanyService();

    const [page, setPage] = useState(1);
    const [data, setData] = useState(null);
    const [alertData, setAlertData] = useState({
        open: false,
        message: '',
        severity: '',
    });
    const [selectedRow, setSelectedRow] = useState(null);
    const [tableLoading, setTableLoading] = useState(true);
    const [loadingRowId, setLoadingRowId] = useState(null);
    const [openUserForm, setOpenUserForm] = useState(false);

    const handleGetCompanyUsers = async (page) => {
        try {
            setTableLoading(true);
            const response = await getCompanyUsers({ companyId: companyData?.id, page });
            setData(response);
        } catch (error) {
            handleErrorAlert(setAlertData);
        } finally {
            setTableLoading(false);
        }
    };

    const handleUpdateCompanyUserStatus = async (status, row) => {
        try {
            const response = await updateCompanyUserStatus({
                id: row?.id,
                status,
                companyId: companyData?.id,
            });
            handleSuccessAlert(setAlertData, response?.message);
            const updatedResults = data.results.map((user) =>
                user.id === row.id ? { ...user, is_active: status } : user,
            );
            setData({ ...data, results: updatedResults });
        } catch (error) {
            handleErrorAlert(setAlertData);
        } finally {
            setLoadingRowId(null);
        }
    };

    const handleUserFormSuccess = (message) => {
        setOpenUserForm(false);
        handleGetCompanyUsers(page);
        handleSuccessAlert(setAlertData, message);
    };

    const handlePageChange = (pageNumber) => {
        setPage(pageNumber);
        handleGetCompanyUsers(pageNumber);
    };

    const handleEdit = (data) => {
        setSelectedRow(data);
        setOpenUserForm(true);
    };

    const handleCloseModal = () => {
        setSelectedRow(null);
        setOpenUserForm(false);
    };

    const handleSwitchChange = async (e, row) => {
        setLoadingRowId(row?.id);
        handleUpdateCompanyUserStatus(e.target.checked, row);
    };

    useEffect(() => {
        if (companyData?.id) {
            handleGetCompanyUsers(page);
        }
    }, [companyData?.id, page]);

    return (
        <Box sx={classes.root}>
            {companyData && data ? (
                <Fragment>
                    <Grid container>
                        <Grid item xs={6}>
                            <Typography
                                text="Company Users"
                                customStyles={{
                                    fontWeight: 700,
                                    fontSize: 18,
                                }}
                            />
                            <Typography
                                text={'Here you can create and update company users.'}
                                customStyles={{
                                    fontWeight: 500,
                                    fontSize: 14,
                                    color: (theme) => theme.primary.dark,
                                }}
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <Box sx={classes.buttonWrapper}>
                                <Button
                                    text={
                                        <Typography
                                            text={'Add User'}
                                            customStyles={classes.addUserButtonText}
                                        />
                                    }
                                    startIcon={<AddIcon htmlColor={'#FFF'} />}
                                    customStyles={classes.addUserButton}
                                    onClick={() => setOpenUserForm(true)}
                                />
                            </Box>
                        </Grid>
                    </Grid>

                    <Box width={'100%'}>
                        <Divider />
                    </Box>

                    <Box width="100%">
                        <Grid container>
                            <Grid item xs={12}>
                                <DataTable
                                    headCells={userTableHeaders({
                                        handleEdit,
                                        loadingRowId,
                                        handleSwitchChange,
                                    })}
                                    isLoading={tableLoading}
                                    data={data.results}
                                ></DataTable>
                            </Grid>
                            <Grid item xs={12} p={'10px 0'}>
                                <TablePagination
                                    page={page}
                                    count={data.count}
                                    onChange={(e, pageNumber) => handlePageChange(pageNumber)}
                                />
                            </Grid>
                        </Grid>
                    </Box>
                    {openUserForm && (
                        <UserForm
                            open={openUserForm}
                            initialValues={selectedRow}
                            type={selectedRow ? 'Edit' : 'Add'}
                            handleCloseModal={handleCloseModal}
                            handleUserFormSuccess={handleUserFormSuccess}
                        />
                    )}
                    <SnackBarAlert alertData={alertData} setAlertData={setAlertData} />
                </Fragment>
            ) : (
                <CircleLoading />
            )}
        </Box>
    );
};

export default Users;
